import { post, patch } from '../../calculator/lib/xhr'
import {
  changeNote,
  initFXCalculator,
  setEnvToInternational,
  setIsLoading,
  showErrorPage,
  storeInternationalDestinationCurrencies,
} from './Actions'
import {
  setFromCurrencyCodeList,
  setToCurrencyCodeList,
  togglePaymentTypeChoiceOverpanel,
  updateIntent,
  updatePaymentDestinationId,
  updatePaymentType,
  updateRecipientCountryCode,
  updateSenderId,
  updateTransactionToken,
  updatePaymentDestinationsMap,
} from './unified-actions'
import { openCurrencyRecipientMismatchOverpanelAction } from './overpanels'
import { PAYMENT_TYPE } from '../../../../lib/constants'
import { fetchFundingOptions } from './send'
import { routeTo } from '../utils/routeHelper'
import { PAYMENT_TYPE_FLOW_MAP, ActionTypes as types } from '../constants'
import { getFlowType } from '../utils/url-utils'
import {
  formatCanonicalAmount,
  getCurrenciesFromPaymentDestinations,
  getPaymentIdByCurrencyCode,
} from '../utils/currencyUtils'
import _ from 'lodash'

export async function createToken() {
  const response = await post('/myaccount/transfer/token')
  return response.data
}

export async function updateToken(state) {
  const {
    amount: { amount, currencyCode },
    crossBorder: {
      contact,
      flowType,
      lastTransactionType,
      toCurrencyCode,
      rate,
      senderAccountId,
      intent,
      paymentType,
      paymentDestinationId,
    },
    recipient,
    note: { note },
  } = state

  const updateTokenPayload = {
    token: recipient.token,
    payerId: contact.payerId,
    networkId: contact.networkId,
    recipient: recipient.recipient,
    senderAccountId,
    amount: {
      amount,
      currencyCode,
    },
    note,
    paymentType,
    intent,
    paymentDestinationId,
    transactionDetails: {
      flowType,
      lastTransactionType,
      toCurrencyCode,
      rate,
    },
  }

  return patch('/myaccount/transfer/token', updateTokenPayload)
}

export function updateInternationalTransactionData() {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true))
    let updateTokenResponse
    try {
      updateTokenResponse = await updateToken(getState())
    } catch (err) {
      dispatch(setIsLoading(false))
      return dispatch(showErrorPage(err))
    }
    dispatch(setIsLoading(false))
    dispatch(
      updateInternationalTransaction(updateTokenResponse.data.recipientDetails)
    )
  }
}

export function updateRecipientDetails(state) {
  return updateToken(state)
}

export function updateSenderAndReceiverCurrencies(responseData) {
  return dispatch => {
    const {
      token: {
        paymentCapabilities: {
          availablePaymentDestinations = [],
          supportedSenderCurrencies = [],
        } = {},
      } = {},
      recipientDetails: { recipientPaymentDestinationsMap = {} } = {},
    } = responseData

    dispatch(updatePaymentDestinationsMap({ recipientPaymentDestinationsMap }))

    const recipientCurrencies = getCurrenciesFromPaymentDestinations(
      availablePaymentDestinations
    )
    dispatch(setToCurrencyCodeList(recipientCurrencies))

    dispatch(
      storeInternationalDestinationCurrencies(availablePaymentDestinations)
    )
    dispatch(setFromCurrencyCodeList(supportedSenderCurrencies))
  }
}

export function startSendInternational() {
  return async (dispatch, getStore) => {
    dispatch(setIsLoading(true))
    let createTokenResponse
    try {
      createTokenResponse = await createToken()
    } catch (err) {
      dispatch(setIsLoading(false))
      dispatch(showErrorPage(err))
      return
    }
    dispatch(setIsLoading(false))

    dispatch(setEnvToInternational())
    dispatch(updateTransactionToken({ token: createTokenResponse.token.id }))
    dispatch(updateSenderAndReceiverCurrencies(createTokenResponse))
    dispatch(updateSenderData(createTokenResponse.token))
  }
}

export function updateInternationalTransaction(tokenData) {
  return dispatch => {
    const defaultPaymentType = getDefaultPaymentType(tokenData)
    dispatch(updateSenderAndReceiverCurrencies(tokenData))
    dispatch(setDefaultPaymentType(defaultPaymentType))

    const isCurrencyMismatch = hasCurrencyMismatch(tokenData)
    if (isCurrencyMismatch) {
      dispatch(
        updateDisplayNameForMismatchCurrency(tokenData.receiver.displayName)
      )
      return dispatch(setInternationalDefaultTransaction(tokenData))
    }
    return dispatch(handleInternationalPaymentType())
  }
}

export function hasCurrencyMismatch(tokenData) {
  const nextStep = tokenData.actionDetails?.nextStep
  return nextStep === 'DISPLAY_CURRENCY_EXCHANGE_OPTION'
}

export function handleInternationalPaymentType() {
  return async (dispatch, getState) => {
    const {
      transaction: { preselectedFundingOption },
      crossBorder: { defaultPaymentType },
    } = getState()

    if (
      [PAYMENT_TYPE.PERSONAL, PAYMENT_TYPE.PURCHASE].includes(
        defaultPaymentType
      )
    ) {
      dispatch(fetchFundingOptions({ preselectedFundingOption }))

      // Ensure spinner does not dissapear in the time between funding options are fecthed ad
      // the redirection happens. This is currently an issue in regular send money too, because
      // the loading reducer for RECEIVED_FUNDING_OPTIONS removes the spinner.
      dispatch(setIsLoading(true))

      return routeTo(
        `/homepage/${PAYMENT_TYPE_FLOW_MAP[defaultPaymentType]}/preview`
      )
    }

    const flowType = getFlowType()
    if (flowType === 'buy' || flowType === 'send') {
      return dispatch(
        fetchFundingOptions({
          preselectedFundingOption,
        })
      )
    }

    return dispatch(togglePaymentTypeChoiceOverpanel({ show: true }))
  }
}

export function setInternationalDefaultTransaction(tokenData) {
  return async dispatch => {
    const { calculatedExchangeRates = {} } = tokenData
    const { receiverAmount = {}, targetCountryCode } = calculatedExchangeRates

    const toCurrencyCode = receiverAmount.currencyCode
    const localizedAmount = formatCanonicalAmount({
      amount: receiverAmount.value,
      currencyCode: toCurrencyCode,
      griffinPresetKey: 'CURRENCY_FORMAT',
    })

    const updatedSendInternationalProps = {
      toAmount: localizedAmount,
      toCurrencyCode,
      targetCountryCode,
    }

    await dispatch(updateInternationalSentAmount(updatedSendInternationalProps))
    dispatch(openCurrencyRecipientMismatchOverpanelAction())
  }
}

export function updateInternationalSentAmount({
  toAmount,
  toCurrencyCode,
  targetCountryCode,
  note,
}) {
  return async dispatch => {
    dispatch(initFXCalculator(toAmount, toCurrencyCode))
    dispatch(changePaymentDestinationId(toCurrencyCode))

    if (targetCountryCode) {
      dispatch(updateRecipientCountryCode(targetCountryCode))
    }

    if (note !== undefined && note !== null) {
      dispatch(changeNote(note))
    }
  }
}

export function updateSenderData(tokenData) {
  return dispatch => {
    const { sender, intent, paymentType } = tokenData
    const defaultPaymentType = getDefaultPaymentType(tokenData)
    dispatch(updateSenderId(sender.accountId))
    dispatch(updateIntent(intent))
    dispatch(updatePaymentType(paymentType))
    dispatch(setDefaultPaymentType(defaultPaymentType))
  }
}

export function changePaymentDestinationId(currencyCode) {
  return (dispatch, getState) => {
    const {
      crossBorder: { availablePaymentDestinations = [] },
    } = getState()

    const paymentDestinationId = getPaymentIdByCurrencyCode(
      availablePaymentDestinations,
      currencyCode
    )

    if (paymentDestinationId) {
      dispatch(updatePaymentDestinationId(paymentDestinationId))
    }
  }
}

export function updateDisplayNameForMismatchCurrency(displayName) {
  return {
    type: types.RECIPIENT.CHANGE_DISPLAY_NAME,
    data: { displayName },
  }
}

export function setDefaultPaymentType(defaultPaymentType) {
  return {
    type: types.CROSS_BORDER.DEFAULT_PAYMENT_TYPE,
    data: { defaultPaymentType },
  }
}

export function getDefaultPaymentType(tokenData) {
  return _.get(
    tokenData,
    'experienceContext.paymentSelectionExperience.defaultPaymentType'
  )
}
